// @flow
/**
 * Proyecciones ligeras de las listas que la home guarda en su estado inicial, el que viaja entero en
 * el HTML. Las tarjetas de la home pintan título, imagen, fecha y entradilla (de `body.metadata`),
 * nunca el cuerpo; con él, el estado inicial pasaba de 3 MB (medido el 2026-10-07: 120 cuerpos de
 * artículo, 805 KB, y las respuestas enteras de cada pregunta, 170 KB).
 *
 * Se quita solo lo que nadie lee y lo demás se deja tal cual: `body.url` (enlaces de vídeo y
 * externos), `body.metadata` de los artículos (entradilla y noindex) y del autor lo que deciden los
 * enlaces (`alias`, `subtype` y `features`, que miran getArticleRel y ReviewCute). Los vídeos y
 * podcasts ya llegan recortados de models.video.
 */

/** Sin los avisos legales ni las cifras de redes de la gestora, que no pinta ningún bloque de la home. */
export const lightOrganization = ({ disclaimers, socialStats, ...organization }: Object) => organization;

/**
 * Autor sin su ficha (descripción, especialidades, certificaciones, avisos legales y cifras de redes)
 * y con sus organizaciones sin avisos, descripción ni redes: ninguna tarjeta de la home los pinta, y
 * en los contenidos de una gestora, que firma ella misma, sus avisos se repetían en cada artículo.
 */
export const lightAuthor = (author: ?Object) => {
  if (!author) return author;

  const { description, specializations, certifications, disclaimers, socialStats, ...rest } = author;

  if (!rest.organizations) return rest;

  return {
    ...rest,
    organizations: rest.organizations.map(({ description: about, ...organization }) => lightOrganization(organization))
  };
};

/** Sin el texto del cuerpo ni sus FAQs, y con el autor ligero. */
export const lightArticle = (article: Object) => ({
  ...article,
  body: article.body ? { ...article.body, text: '', faqs: [] } : article.body,
  author: lightAuthor(article.author)
});

export const lightArticles = (articles: Array<Object>): Array<Object> => articles.map(lightArticle);

/**
 * Sin el cuerpo (ni su texto ni la copia de draft-js de `metadata`) ni los comentarios, y cada
 * respuesta reducida a su id: la tarjeta de la home (SmallQuestion) pinta el título y cuenta las
 * respuestas.
 */
export const lightQuestion = (question: Object) => ({
  ...question,
  body: question.body ? { ...question.body, text: '', metadata: {} } : question.body,
  answers: (question.answers || []).map(({ id }) => ({ id })),
  comments: []
});

export const lightQuestions = (questions: Array<Object>): Array<Object> => questions.map(lightQuestion);

/**
 * Las reviews sin su texto, que ReviewCute no pinta (título, nota, producto y autor), y sus autores
 * ligeros: de la primera organización sale la empresa que firma.
 */
export const lightReviews = (data: Object) => ({
  ...data,
  reviews: (data.reviews || []).map(({ body, ...review }) => review),
  authors: (data.authors || []).map(lightAuthor)
});

/**
 * Producto sin las series mensual y trimestral de rentabilidad: las tarjetas de la home (ProductSummary)
 * solo leen `stats.performance.periods`, y las series eran la mitad del peso de los productos.
 */
export const lightProduct = (product: Object) => {
  const { stats } = product.entity;

  if (!stats || !stats.performance) return product;

  const { months, quarters, ...performance } = stats.performance;

  return { ...product, entity: { ...product.entity, stats: { ...stats, performance } } };
};
